import { onDestroy, onMount } from "@/modules/_";
import gsap, { reduced } from "@lib/gsap";
import { gsapEase } from "@/utils/easings";
import { Raf } from "@lib/subs";

const MIN_SCALE = 0.25;
const MAX_SCALE = 1;
const MIN_DURATION = 0.32;
const MAX_DURATION = 0.78;
const EASE = gsapEase("smooth");

// Idle "breathing" when not playing — alive but clearly calmer than playing.
const IDLE_MIN_SCALE = 0.14;
const IDLE_MAX_SCALE = 0.4;
const IDLE_MIN_DURATION = 1.1;
const IDLE_MAX_DURATION = 2.0;

const POLL_MS = 30_000;
const DISC_SPIN_SEC = 14; // seconds per full vinyl rotation while playing
const DISC_IDLE_SPIN_SEC = 36; // slower idle drift when not playing
// Fallback now-playing endpoint. Override per-element with
// data-endpoint="https://clay-spotify.<subdomain>.workers.dev/now-playing".
const DEFAULT_ENDPOINT =
  "https://clay-spotify.clay-run.workers.dev/now-playing";

interface NowPlaying {
  isPlaying: boolean;
  source: "current" | "recent" | "none";
  track: string | null;
  artist: string | null;
  album: string | null;
  art: string | null;
  url: string | null;
  artistUrl: string | null;
  albumUrl: string | null;
  progressMs: number | null;
  durationMs: number | null;
  playedAt: string | null;
  fetchedAt: number;
}

function formatTime(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr${hr === 1 ? "" : "s"} ago`;
  const day = Math.round(hr / 24);
  if (day === 1) return "yesterday";
  if (day < 7) return `${day} days ago`;
  return new Date(iso).toLocaleDateString();
}

export default function (element: HTMLElement, dataset: DOMStringMap) {
  const lines = Array.from(
    element.querySelectorAll<HTMLElement>('[util-music="wave-line"]'),
  );
  const waveW = element.querySelector<HTMLElement>('[util-music="wave-w"]');
  // Decorative — the artist/song labels carry the meaning.
  waveW?.setAttribute("aria-hidden", "true");

  // Data hooks. Only artist + song exist in the base markup; the rest are
  // optional and no-op gracefully until added in Webflow.
  const artistEl = element.querySelector<HTMLElement>('[util-music="artist"]');
  const songEl = element.querySelector<HTMLElement>('[util-music="song"]');
  const artEl = element.querySelector<HTMLElement>('[util-music="art"]');
  const discEl = element.querySelector<HTMLElement>('[util-music="disc"]');
  const linkEl = element.querySelector<HTMLAnchorElement>('[util-music="link"]');
  const progressEl = element.querySelector<HTMLElement>('[util-music="progress"]');
  const elapsedEl = element.querySelector<HTMLElement>('[util-music="elapsed"]');
  const durationEl = element.querySelector<HTMLElement>('[util-music="duration"]');
  const playedEl = element.querySelector<HTMLElement>('[util-music="played"]');

  const endpoint = dataset.endpoint || DEFAULT_ENDPOINT;

  let killed = false;
  let playing = false;
  let pollTimer: number | null = null;
  let controller: AbortController | null = null;
  let rafOff: (() => void) | null = null;
  let lastArt: string | null = null;
  let spinTween: gsap.core.Tween | null = null;
  const discSpeed = { ts: 1 }; // proxy for smoothly ramping disc rotation speed
  let songUrl: string | null = null;
  let artistLinkUrl: string | null = null;
  let albumUrl: string | null = null;
  const linkCleanups: (() => void)[] = [];

  // Progress interpolation state.
  let progressBase = 0; // progressMs reported at last fetch
  let durationMs = 0;
  let localBase = 0; // performance.now() at last fetch
  let liveProgress = false; // playing && we have progress + duration

  // ---------- waveform (driven by real play state) ----------
  function pulse(line: HTMLElement) {
    if (killed || !playing) return;
    gsap.to(line, {
      scaleY: gsap.utils.random(MIN_SCALE, MAX_SCALE),
      duration: gsap.utils.random(MIN_DURATION, MAX_DURATION),
      ease: EASE,
      onComplete: () => pulse(line),
    });
  }

  function startWave() {
    if (reduced || lines.length === 0) return;
    lines.forEach((line) => {
      gsap.killTweensOf(line);
      pulse(line);
    });
  }

  // Idle breathing loop — runs only while NOT playing (mirrors pulse()).
  function pulseIdle(line: HTMLElement) {
    if (killed || playing) return;
    gsap.to(line, {
      scaleY: gsap.utils.random(IDLE_MIN_SCALE, IDLE_MAX_SCALE),
      duration: gsap.utils.random(IDLE_MIN_DURATION, IDLE_MAX_DURATION),
      ease: "sine.inOut",
      onComplete: () => pulseIdle(line),
    });
  }

  function stopWave() {
    if (reduced || lines.length === 0) return;
    lines.forEach((line) => {
      gsap.killTweensOf(line);
      pulseIdle(line);
    });
  }

  // Vinyl disc always turns: full speed while playing, eased down to a slow
  // idle drift when stopped (never fully freezes).
  function spin(active: boolean) {
    if (reduced || !discEl) return;
    if (!spinTween) {
      spinTween = gsap.to(discEl, {
        rotation: "+=360",
        duration: DISC_SPIN_SEC,
        ease: "none",
        repeat: -1,
      });
    }
    spinTween.play();
    gsap.to(discSpeed, {
      ts: active ? 1 : DISC_SPIN_SEC / DISC_IDLE_SPIN_SEC,
      duration: 0.9,
      ease: "power2.out",
      overwrite: true,
      onUpdate: () => spinTween?.timeScale(discSpeed.ts),
    });
  }

  function setPlaying(next: boolean) {
    if (next === playing) return;
    playing = next;
    if (reduced) return;
    spin(playing);
    if (playing) startWave();
    else stopWave();
  }

  // ---------- progress bar ----------
  function renderProgress(elapsedMs: number) {
    if (progressEl && durationMs > 0) {
      gsap.set(progressEl, { scaleX: Math.min(1, elapsedMs / durationMs) });
    }
    if (elapsedEl) elapsedEl.textContent = formatTime(elapsedMs);
    if (durationEl && durationMs > 0) {
      durationEl.textContent = formatTime(durationMs);
    }
  }

  function startProgressLoop() {
    if (rafOff || reduced) return;
    rafOff = Raf.add(() => {
      if (!liveProgress) return;
      const elapsed = Math.min(
        durationMs,
        progressBase + (performance.now() - localBase),
      );
      renderProgress(elapsed);
    });
  }

  function stopProgressLoop() {
    if (rafOff) {
      rafOff();
      rafOff = null;
    }
  }

  // ---------- album art (crossfade on change) ----------
  function setArt(url: string) {
    if (!artEl || url === lastArt) return;
    lastArt = url;

    const swap = () => {
      if (artEl instanceof HTMLImageElement) {
        // Webflow adds a responsive srcset/sizes that would override src — clear them.
        artEl.srcset = "";
        artEl.sizes = "";
        artEl.alt = ""; // decorative: the disc link + song text carry the meaning
        artEl.src = url;
      } else {
        artEl.style.backgroundImage = `url("${url}")`;
      }
    };

    if (reduced) {
      swap();
      return;
    }

    // Preload so we fade to a ready image (no flash), then crossfade.
    const pre = new Image();
    pre.onload = () => {
      gsap.to(artEl, {
        opacity: 0,
        duration: 0.25,
        ease: "power2.in",
        onComplete: () => {
          swap();
          gsap.to(artEl, { opacity: 1, duration: 0.45, ease: "power2.out" });
        },
      });
    };
    pre.onerror = swap; // if preload fails, just swap without the fade
    pre.src = url;
  }

  // ---------- click-through (song→track, artist→artist, disc→album) ----------
  function setupLink(
    el: HTMLElement | null,
    getHref: () => string | null,
    label: string,
  ) {
    if (!el) return;
    el.setAttribute("role", "link");
    el.setAttribute("tabindex", "0");
    el.setAttribute("aria-label", label);
    const open = () => {
      const href = getHref();
      if (href) window.open(href, "_blank", "noopener");
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        open();
      }
    };
    el.addEventListener("click", open);
    el.addEventListener("keydown", onKey);
    linkCleanups.push(() => {
      el.removeEventListener("click", open);
      el.removeEventListener("keydown", onKey);
    });
  }

  // A link is only "live" when it has a destination: show the pointer, keep it
  // tabbable, and drop aria-disabled. Otherwise make it inert + non-focusable.
  function setLinkState(el: HTMLElement | null, url: string | null) {
    if (!el) return;
    el.style.cursor = url ? "pointer" : "";
    el.setAttribute("tabindex", url ? "0" : "-1");
    if (url) el.removeAttribute("aria-disabled");
    else el.setAttribute("aria-disabled", "true");
  }

  function refreshCursors() {
    setLinkState(songEl, songUrl);
    setLinkState(artistEl, artistLinkUrl);
    setLinkState(discEl, albumUrl);
  }

  // ---------- render a payload ----------
  function apply(data: NowPlaying) {
    if (artistEl && data.artist) {
      if (artistEl.textContent !== data.artist) artistEl.textContent = data.artist;
      artistEl.title = data.artist; // full text on hover when truncated
      artistEl.setAttribute("aria-label", `Open artist ${data.artist} on Spotify`);
    }
    if (songEl && data.track) {
      if (songEl.textContent !== data.track) songEl.textContent = data.track;
      songEl.title = data.track;
      songEl.setAttribute("aria-label", `Open song ${data.track} on Spotify`);
    }
    if (discEl && data.album) {
      discEl.setAttribute("aria-label", `Open album ${data.album} on Spotify`);
    }
    if (playedEl) {
      playedEl.textContent =
        data.source === "recent" && data.playedAt
          ? `Last played ${relativeTime(data.playedAt)}`
          : "";
    }
    if (linkEl && data.url) linkEl.href = data.url;
    if (data.art) setArt(data.art);

    songUrl = data.url;
    artistLinkUrl = data.artistUrl;
    albumUrl = data.albumUrl;
    refreshCursors();

    element.setAttribute(
      "data-state",
      data.source === "none"
        ? "offline"
        : data.isPlaying
          ? "playing"
          : data.source === "recent"
            ? "recent"
            : "paused",
    );

    durationMs = data.durationMs ?? 0;
    progressBase = data.progressMs ?? 0;
    localBase = performance.now();
    liveProgress = data.isPlaying && data.progressMs != null && durationMs > 0;

    if (liveProgress && !reduced) startProgressLoop();
    else {
      stopProgressLoop();
      if (durationMs > 0) renderProgress(progressBase);
    }

    setPlaying(data.isPlaying);
  }

  // ---------- polling ----------
  async function poll() {
    controller?.abort();
    controller = new AbortController();
    try {
      const res = await fetch(endpoint, { signal: controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as NowPlaying;
      if (!killed) apply(data);
    } catch (err) {
      if (err instanceof Error && err.name !== "AbortError") {
        console.warn("[util-music] poll failed:", err.message);
      }
    }
  }

  function onVisibility() {
    if (document.hidden) {
      if (pollTimer !== null) {
        window.clearInterval(pollTimer);
        pollTimer = null;
      }
    } else {
      poll();
      if (pollTimer === null) pollTimer = window.setInterval(poll, POLL_MS);
    }
  }

  onMount(() => {
    gsap.set(lines, { transformOrigin: "center center" });
    if (discEl) gsap.set(discEl, { transformOrigin: "center center" });
    if (progressEl) gsap.set(progressEl, { transformOrigin: "left center", scaleX: 0 });

    if (reduced) {
      gsap.set(lines, { scaleY: 1 });
    } else {
      gsap.set(lines, { scaleY: gsap.utils.random(MIN_SCALE, MAX_SCALE) });
      // Animate by default until the first payload tells us the real state.
      playing = true;
      if (lines.length > 0) startWave();
      spin(true);
    }

    setupLink(songEl, () => songUrl, "Open song on Spotify");
    setupLink(artistEl, () => artistLinkUrl, "Open artist on Spotify");
    setupLink(discEl, () => albumUrl, "Open album on Spotify");

    poll();
    pollTimer = window.setInterval(poll, POLL_MS);
    document.addEventListener("visibilitychange", onVisibility);
  });

  onDestroy(() => {
    killed = true;
    if (pollTimer !== null) window.clearInterval(pollTimer);
    controller?.abort();
    stopProgressLoop();
    document.removeEventListener("visibilitychange", onVisibility);
    linkCleanups.forEach((fn) => fn());
    gsap.killTweensOf(lines);
    gsap.set(lines, { clearProps: "transform" });
    if (artEl) {
      gsap.killTweensOf(artEl);
      gsap.set(artEl, { clearProps: "opacity" });
    }
    if (discEl) {
      spinTween?.kill();
      spinTween = null;
      gsap.killTweensOf(discSpeed);
      gsap.set(discEl, { clearProps: "rotation,transform" });
    }
    waveW?.removeAttribute("aria-hidden");
  });
}
